import { type ReactNode, useState } from 'react';
import { cssVar } from '@going-once/tokens';
import { Dialog } from '../ui/dialog';
import { TableToolbar } from '../ui/filters';
import { PageHeader } from '../ui/frame';
import { Menu, type MenuGroup } from '../ui/menu';
import { Avatar, Badge, type BadgeVariant, Pressable, Stack, Text } from '../ui/primitives';
import { EmptyState, Toast, useToast } from '../ui/state';
import { ActionsCell, PersonCell, Table, type TableColumn } from '../ui/table';
import { TabPanel, Tabs } from '../ui/tabs';

/**
 * The admin Sellers page's primitives (GO-613), each in the states it has, so the page that
 * uses them — and design — can see them without a seller to look at. Every row here is
 * made up; nothing on this page calls the API.
 */

/** A stand-in photo that is a picture without being a color decision: a head and shoulders, in the default ink. */
const PHOTO =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3E%3Ccircle cx='4' cy='3' r='2'/%3E%3Cpath d='M0 8a4 4 0 0 1 8 0z'/%3E%3C/svg%3E";

type Status = 'onboarding' | 'active' | 'suspended';

interface Row {
  id: string;
  name: string;
  handle: string;
  status: Status;
  gmv: number;
  photo?: string;
}

const ROWS: Row[] = [
  { id: 's1', name: 'Luna Vintage', handle: '@lunavintage', status: 'active', gmv: 48210, photo: PHOTO },
  { id: 's2', name: 'Kickback Kicks', handle: '@kickbackkicks', status: 'onboarding', gmv: 0 },
  { id: 's3', name: 'Top Shelf Toys', handle: '@topshelftoys', status: 'suspended', gmv: 1290 },
];

const STATUS: Record<Status, { label: string; variant: BadgeVariant }> = {
  onboarding: { label: 'Onboarding', variant: 'timed' },
  active: { label: 'Active', variant: 'leading' },
  suspended: { label: 'Suspended', variant: 'quiet' },
};

const CHIPS = [
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
] as const;

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'devices', label: 'Devices & logins' },
  { id: 'audit', label: 'Audit log' },
];

export function AdminPrimitives() {
  const { toast, flash, dismiss } = useToast();

  return (
    <Stack gap="xl">
      <Demo title="Badge" note="leading · timed · quiet · live. Caps by style; there is no size prop.">
        <Stack direction="row" gap="sm" align="center" wrap>
          <Badge variant="leading">Active</Badge>
          <Badge variant="timed">Onboarding</Badge>
          <Badge variant="quiet">Suspended</Badge>
          <Badge variant="quiet">Restricted</Badge>
          <Badge variant="live">Live</Badge>
        </Stack>
      </Demo>

      <Demo
        title="Avatar"
        note="A rounded square everywhere. Row 32, header 56; photo, else initials (a photo that fails to load falls back); ring; live, which links to the show in a new tab."
      >
        <Stack direction="row" gap="lg" align="center" wrap>
          <Avatar name="Luna Vintage" />
          <Avatar name="Luna Vintage" src={PHOTO} />
          <Avatar name="Luna Vintage" size="header" />
          <Avatar name="Luna Vintage" size="header" src={PHOTO} />
          <Avatar name="Broken Photo" size="header" src="/no-such-photo.jpg" />
          <Avatar name="Luna Vintage" size="header" ring />
          <Avatar
            name="Luna Vintage"
            size="header"
            live={{ href: '/design', label: 'Watch Luna Vintage’s live show (opens in a new tab)' }}
          />
        </Stack>
      </Demo>

      <Demo title="Tabs" note="Arrow keys move and select; Home and End; the selected tab is the only tab stop.">
        <TabsDemo />
      </Demo>

      <Demo
        title="Menu"
        note="Groups split by a rule; danger last. Enter, Space or ↓ opens it; ↑ ↓ Home End move; Escape closes and returns focus. With no items it draws nothing — the empty one is between the brackets."
      >
        <Stack direction="row" gap="lg" align="center" wrap>
          <Menu label="More actions" groups={sellerMenu(flash)} />
          <Text step="mono" tone="fg.muted">
            [<Menu label="Nothing to offer" groups={[[], []]} />]
          </Text>
        </Stack>
      </Demo>

      <Demo
        title="Dialog"
        note="Blocking without close: no ✕, Escape and the scrim do nothing. With close: ✕, Escape and the scrim close it. Busy: nothing closes it."
      >
        <DialogDemo flash={flash} />
      </Demo>

      <Demo
        title="TableToolbar and Table"
        note="Search with clear, single-select chips (press the selected one again for all), the live count, no Export. Rows open from anywhere on them, or from the row header's button by keyboard; the open row is selected. 30d GMV sorts because this caller sorts; the table never does."
      >
        <TableDemo flash={flash} />
      </Demo>

      <Demo
        title="PageHeader"
        note="A list page is its title alone. A record page: linked crumbs, lead, title, badge, meta, secondary actions, ••• and tabs."
      >
        <Stack gap="xl">
          <PageHeader title="Sellers" crumbs={[]} />
          <HeaderDemo flash={flash} />
        </Stack>
      </Demo>

      <Demo title="EmptyState">
        <Stack direction="row" gap="lg" wrap>
          <EmptyState icon="search" title="Search for a seller by name or handle" />
          <EmptyState
            icon="scroll-text"
            title="No staff actions yet"
            body="Anything Going Once staff do here shows up in this log."
          />
          <EmptyState
            icon="search-x"
            title="No results"
            body="Nothing matches these filters. Clear them to see everything."
            action={{ label: 'Clear filters', onPress: () => flash('Filters cleared.') }}
          />
        </Stack>
      </Demo>

      <Demo title="Toast" note="Success, bottom-left, gone after 4 s; it waits while the pointer or focus is on it.">
        <Stack direction="row" gap="sm">
          <Pressable variant="secondary" onPress={() => flash('Luna Vintage is active again.')}>
            Show a toast
          </Pressable>
        </Stack>
      </Demo>

      <Toast toast={toast} onDismiss={dismiss} />
    </Stack>
  );
}

function sellerMenu(flash: (message: string) => void): MenuGroup[] {
  return [
    [
      { icon: 'log-out', label: 'Log out of all devices', onSelect: () => flash('Log out of all devices.') },
      { label: 'Change trust tier', onSelect: () => flash('Change trust tier.') },
      { label: 'Disabled item', disabled: true, onSelect: () => undefined },
    ],
    [{ icon: 'ban', label: 'Suspend seller', tone: 'danger', onSelect: () => flash('Suspend seller.') }],
  ];
}

function TabsDemo() {
  const [tab, setTab] = useState('overview');
  return (
    <Stack gap="md">
      <Tabs label="Gallery sections" tabs={TABS} value={tab} onChange={setTab} idBase="gallery-tabs" />
      <TabPanel idBase="gallery-tabs" tab={tab}>
        <Text tone="fg.muted">The {TABS.find((item) => item.id === tab)?.label} panel.</Text>
      </TabPanel>
    </Stack>
  );
}

function DialogDemo({ flash }: { flash: (message: string) => void }) {
  const [open, setOpen] = useState<'blocking' | 'closable' | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const shut = () => setOpen(undefined);
  return (
    <>
      <Stack direction="row" gap="sm" wrap>
        <Pressable variant="secondary" onPress={() => setOpen('blocking')}>
          Blocking dialog
        </Pressable>
        <Pressable variant="secondary" onPress={() => setOpen('closable')}>
          Closable dialog
        </Pressable>
      </Stack>
      {open === 'blocking' ? (
        <Dialog
          title="Suspend Luna Vintage?"
          busy={busy}
          actions={
            <>
              <Pressable variant="quiet" disabled={busy} onPress={shut}>
                Keep
              </Pressable>
              <Pressable
                variant="danger"
                busy={busy}
                onPress={() => {
                  // A pretend write: two seconds in flight, during which nothing closes it.
                  setBusy(true);
                  setTimeout(() => {
                    setBusy(false);
                    shut();
                    flash('Luna Vintage is suspended for 7 days.');
                  }, 2000);
                }}
              >
                Suspend seller
              </Pressable>
            </>
          }
        >
          <Text tone="fg.muted">
            They can't go live, list or take new orders until the suspension ends. Open orders still ship and stay
            covered.
          </Text>
        </Dialog>
      ) : null}
      {open === 'closable' ? (
        <Dialog
          title="Audit record"
          size="md"
          close={shut}
          actions={
            <Pressable variant="quiet" onPress={shut}>
              Close
            </Pressable>
          }
        >
          <Text tone="fg.muted">Audit records can’t be edited or deleted.</Text>
        </Dialog>
      ) : null}
    </>
  );
}

function TableDemo({ flash }: { flash: (message: string) => void }) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<Status | undefined>(undefined);
  const [order, setOrder] = useState<'descending' | 'ascending' | undefined>(undefined);
  const [open, setOpen] = useState<string | undefined>(undefined);

  const term = search.trim().toLowerCase();
  const shown = ROWS.filter(
    (row) =>
      (status === undefined || row.status === status) && `${row.name} ${row.handle}`.toLowerCase().includes(term),
  ).sort((a, b) => (order === undefined ? 0 : order === 'descending' ? b.gmv - a.gmv : a.gmv - b.gmv));

  const columns: TableColumn<Row>[] = [
    {
      key: 'seller',
      header: 'Seller',
      rowHeader: true,
      minWidth: 'lg',
      cell: (row) => <PersonCell name={row.name} sub={row.handle} avatarUrl={row.photo} />,
    },
    {
      key: 'status',
      header: 'Status',
      width: 'sm',
      cell: (row) => <Badge variant={STATUS[row.status].variant}>{STATUS[row.status].label}</Badge>,
    },
    {
      key: 'gmv',
      header: '30d GMV',
      numeric: true,
      nowrap: true,
      width: 'sm',
      ...(order ? { sorted: order } : {}),
      onSort: () => setOrder((current) => (current === 'descending' ? 'ascending' : 'descending')),
      cell: (row) => <Text>${row.gmv.toLocaleString('en-US')}</Text>,
    },
    {
      key: 'act',
      header: 'Actions',
      hideHeader: true,
      width: 'actions',
      // Kickback Kicks shows the empty cell: a row this caller may do nothing to.
      cell: (row) => (
        <ActionsCell label={`More options for ${row.name}`} groups={row.id === 's2' ? [] : sellerMenu(flash)} />
      ),
    },
  ];

  return (
    <Stack gap="md">
      <TableToolbar<Status>
        search={search}
        onSearch={setSearch}
        placeholder="Search by name or handle"
        chips={{ choices: CHIPS, value: status, onChange: setStatus }}
        count={`${shown.length} ${shown.length === 1 ? 'seller' : 'sellers'}`}
        exportable={false}
      />
      <Table
        caption="Sellers"
        hideCaption
        columns={columns}
        rows={shown}
        rowKey={(row) => row.id}
        onRowOpen={(row) => setOpen(row.id)}
        selectedKey={open}
        empty={
          <EmptyState
            icon="search-x"
            title="No results"
            body="Nothing matches these filters. Clear them to see everything."
          />
        }
      />
    </Stack>
  );
}

function HeaderDemo({ flash }: { flash: (message: string) => void }) {
  const [tab, setTab] = useState('overview');
  return (
    <Stack gap="md">
      <PageHeader
        title="Luna Vintage"
        crumbs={[{ label: 'Sellers', href: '/design', onPress: () => flash('Back to Sellers.') }, 'Luna Vintage']}
        lead={
          <Avatar
            name="Luna Vintage"
            size="header"
            live={{ href: '/design', label: 'Watch Luna Vintage’s live show (opens in a new tab)' }}
          />
        }
        badge={<Badge variant="leading">Active</Badge>}
        meta="@lunavintage · Owner Rosa Delgado · Joined Mar 4, 2026"
        secondary={[{ label: 'Secondary action', icon: 'log-out', onPress: () => flash('Secondary action.') }]}
        more={sellerMenu(flash)}
        tabs={{ items: TABS, value: tab, onChange: setTab, idBase: 'gallery-header' }}
      />
      <TabPanel idBase="gallery-header" tab={tab}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${cssVar('size.card.detail')}), 1fr))`,
            gap: cssVar('space.lg'),
          }}
        >
          <EmptyState title="A card" body="The overview grid: cards at least size.card.detail wide." />
          <EmptyState title="Another card" />
        </div>
      </TabPanel>
    </Stack>
  );
}

function Demo({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <Stack gap="sm">
      <Text as="h3" step="title">
        {title}
      </Text>
      {note ? (
        <Text step="label" tone="fg.muted">
          {note}
        </Text>
      ) : null}
      <div style={{ paddingTop: cssVar('space.xs') }}>{children}</div>
    </Stack>
  );
}
